import React, { useEffect, useState } from 'react';

export const assessmentEvents = [
  ['contact_viewed', 'Contact screen'], ['started', 'Assessment started'],
  ['gender_viewed', 'Gender screen'], ['age_viewed', 'Age screen'],
  ['location_viewed', 'Location screen'], ['q6b_financial_viewed', 'Financial question'],
  ['answer_selected', 'Answer selected'], ['back_clicked', 'Back clicked'],
  ['disqualified_viewed', 'Disqualified poster'], ['qualified_viewed', 'Qualified poster'],
  ...['contact', 'qualified', 'disqualified'].flatMap(stage => [
    [`${stage}_submission_started`, `${stage === 'contact' ? 'Contact-only' : stage === 'qualified' ? 'Qualified' : 'Disqualified'} submission started`],
    [`${stage}_submission_succeeded`, `${stage === 'contact' ? 'Contact-only' : stage === 'qualified' ? 'Qualified' : 'Disqualified'} submission succeeded`],
    [`${stage}_submission_failed`, `${stage === 'contact' ? 'Contact-only' : stage === 'qualified' ? 'Qualified' : 'Disqualified'} submission failed`],
  ]),
  ['submission_started', 'Legacy mixed submission started'], ['submission_succeeded', 'Legacy mixed submission succeeded'],
  ['submission_failed', 'Legacy mixed submission failed'], ['video_viewed', 'Video screen'],
  ['video_started', 'Video started'], ['video_completed', 'Video completed'],
  ['video_failed', 'Video failed'], ['continue_viewed', 'Continue screen'],
  ['continue_clicked', 'Continue to Site clicked'],
].map(([suffix, label]) => [`assessment_private_${suffix}`, label]);

export async function fetchAssessmentEvents(filters, signal) {
  const params = new URLSearchParams(filters);
  const response = await fetch(`/api/sirri_api/assessment_events?${params}`, {
    credentials: 'same-origin', cache: 'no-store', signal,
  });
  if (!response.ok) throw new Error(response.status === 403 ? 'Admin session required. Please sign in again.' : `Unable to load events (HTTP ${response.status}).`);
  if (!(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Expected JSON from the assessment events API.');
  const data = await response.json();
  if (!data || data.responsecode !== 200 || !Array.isArray(data.rows)) throw new Error(data?.message || 'Invalid assessment events response.');
  return data;
}

function formatTime(row) {
  const epoch = Number(row.server_received_epoch);
  return Number.isFinite(epoch) && epoch > 0 ? new Date(epoch * 1000).toISOString().replace('T', ' ').replace('.000Z', ' UTC') : 'Not recorded';
}

export function AssessmentEventsReport({ data }) {
  if (!data) return <p>Load a date and domain to view recorded assessment events.</p>;
  const available = data.storage_available === true;
  const rows = data.rows || [];
  return <>
    {!available && <p role="status" className="bg-yellow-950 p-3 rounded">Bridge log data is unavailable for this date. This does not establish that no activity occurred. Check the configured bridge storage/mount.</p>}
    {(data.warnings || []).map((warning, index) => <p key={index} className="text-yellow-200">{warning}</p>)}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 my-4">
      {[
        ['Recorded events', data.total_events], ['Distinct recorded visitors', data.unique_visitors],
        ['Distinct recorded sessions', data.unique_sessions],
      ].map(([label, value]) => <div key={label} className="bg-gray-900 rounded p-4"><p>{label}</p><strong className="text-2xl">{available ? value ?? '—' : '—'}</strong></div>)}
    </div>
    <p className="text-sm text-gray-300">UTC date: {data.date} · Domain: {data.host || 'All domains'}. Counts cover the full selected day, not just the recent rows. Repeat visits count again; visitors/sessions count only recorded identifiers.</p>
    <h2 className="text-xl my-3">Stage and action counts</h2>
    <p className="text-sm text-gray-300 mb-3">Contact-only submissions save the first-step details; qualified submissions follow all passing answers; disqualified submissions save a rejection. Legacy mixed submission events cannot reliably be separated by status.</p>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {assessmentEvents.map(([name, label]) => <div key={name} className="border border-gray-700 rounded p-3">
        <div className="flex justify-between gap-3"><span>{label}</span><strong>{available ? data.counts?.[name] || 0 : '—'}</strong></div>
        <code className="text-xs text-gray-400 break-all">{name}</code>
      </div>)}
    </div>
    <h2 className="text-xl mt-6 mb-2">Recent events</h2>
    <p className="text-sm mb-3">Showing {rows.length} of {data.total_events ?? '—'} recorded events{data.has_more ? ' — increase the row limit to see more.' : '.'}</p>
    {rows.length === 0 ? <p>{available ? 'No matching assessment events recorded for these filters.' : 'No readable records available.'}</p> :
      <div className="overflow-x-auto"><table className="w-full text-sm text-left">
        <thead><tr>{['Time (UTC)', 'Event / details', 'Domain / path', 'Visitor / session'].map(label => <th key={label} className="p-2 border-b border-gray-600">{label}</th>)}</tr></thead>
        <tbody>{rows.map((row, index) => <tr key={`${row.server_received_epoch}-${row.event_seq}-${index}`} className="border-b border-gray-800 align-top">
          <td className="p-2 whitespace-nowrap">{formatTime(row)}</td>
          <td className="p-2"><code className="break-all">{row.event_name}</code>
            {['question', 'outcome', 'http_status', 'reason', 'from_step', 'to_step'].filter(key => row[key] !== undefined && row[key] !== '').map(key => <p key={key}>{key}: {String(row[key])}</p>)}
          </td>
          <td className="p-2 break-all">{row.host || 'Not recorded'}<br />{row.path || 'Not recorded'}</td>
          <td className="p-2 break-all">{row.visitor_id || 'Visitor not recorded'}<br />{row.session_id || 'Session not recorded'}<br />Sequence: {row.event_seq || 'Not recorded'}</td>
        </tr>)}</tbody>
      </table></div>}
  </>;
}

export default function AssessmentEventsDashboard() {
  const [host, setHost] = useState('penzi-halisi.com');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [limit, setLimit] = useState('100');
  const [filters, setFilters] = useState(() => ({host: 'penzi-halisi.com', date: new Date().toISOString().slice(0, 10), limit: '100'}));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData(null);
    fetchAssessmentEvents(filters, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      setData(result);
      setLoadedAt(new Date().toLocaleTimeString());
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.message || 'Unable to load events.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, refreshKey]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = window.setInterval(() => setRefreshKey(value => value + 1), 30000);
    return () => window.clearInterval(timer);
  }, [autoRefresh]);

  return <div className="w-full max-w-7xl mx-auto text-white bg-black p-4 rounded">
    <h1 className="text-2xl mb-2">Assessment Events</h1>
    <p className="text-sm text-gray-300 mb-4">Private assessment bridge logs, available before the scheduled Discord digest. Read-only; refreshing does not send Discord messages.</p>
    <form onSubmit={event => { event.preventDefault(); setFilters({host, date, limit}); }} className="flex flex-wrap gap-3 items-end">
      <label>Domain<select className="block p-2 rounded text-black" value={host} onChange={event => setHost(event.target.value)}>
        <option value="">All domains</option>
        {['penzi-halisi.com', 'upendo-halisi.com', 'expatelitesingles.com'].map(domain => <option key={domain} value={domain}>{domain}</option>)}
      </select></label>
      <label>Date (UTC)<input className="block p-2 rounded text-black" type="date" required value={date} onChange={event => setDate(event.target.value)} /></label>
      <label>Recent row limit<select className="block p-2 rounded text-black" value={limit} onChange={event => setLimit(event.target.value)}>
        {['50', '100', '250', '500'].map(value => <option key={value} value={value}>{value}</option>)}
      </select></label>
      <button className="bg-white text-black px-4 py-2 rounded" type="submit" disabled={loading}>{loading ? 'Loading…' : 'Apply / Refresh'}</button>
      <label className="py-2"><input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} /> Auto-refresh every 30 seconds</label>
    </form>
    {loadedAt && <p className="text-xs text-gray-400 mt-2">Last successful refresh: {loadedAt}</p>}
    {error && <p role="alert" className="text-red-300 my-3">{error}</p>}
    {loading ? <p role="status" className="my-4">Loading bridge events…</p> : !error && <AssessmentEventsReport data={data} />}
  </div>;
}
